import React, { PureComponent, memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';

import TableFieldRteCell from './TableFieldRteCell';
import TableFieldMenuCell from './TableFieldMenuCell';

const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledNumberCell = styled.div`
  box-sizing: border-box;
  font-size: ${(props) => props.fontSize || 14}px;
  font-weight: 500;
  text-align: center;
  padding: ${(props) => props.padding || 8}px;
  border-right: ${(props) => props.border};
  border-bottom: ${(props) => props.border};
  max-width: 48px;
  min-width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

class TableFieldRteRow extends PureComponent {

  static propTypes = {
    id: PropTypes.string,
    row: PropTypes.object,
    rowIndex: PropTypes.number,
    rowPath: PropTypes.string,
    rteColumns: PropTypes.array,
    tableColumns: PropTypes.array,
    fieldPositionTableColumns: PropTypes.object,
    fieldMode: PropTypes.string,
    disabled: PropTypes.bool,
    enableNumber: PropTypes.bool,
    enableMenu: PropTypes.bool,
    rowActions: PropTypes.array,
    onRowAction: PropTypes.func,

    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    padding: PropTypes.number,
    borderWidth: PropTypes.number,
    borderColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    primaryColor: PropTypes.string,

    loadCompletedId: PropTypes.string,
    onLoadCompleted: PropTypes.func,
  }

  constructor(props) {
    super(props);
    this.state = { rowRef: null };
    this._lastReportedHeight = null;
  }

  componentDidUpdate() {
    const { onLoadCompleted, loadCompletedId } = this.props;
    const { rowRef } = this.state;
    if (onLoadCompleted && rowRef) {
      const height = rowRef.offsetHeight;
      if (height !== this._lastReportedHeight) {
        this._lastReportedHeight = height;
        onLoadCompleted(loadCompletedId, { height });
      }
    }
  }

  render() {

    const {
      id,
      row,
      rowIndex,
      rowPath,
      rteColumns,
      tableColumns,
      fieldPositionTableColumns,
      fieldMode,
      disabled,
      enableNumber,
      enableMenu,
      rowActions,
      onRowAction,

      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      padding,
      borderWidth,
      borderColor,
      backgroundColor,
      primaryColor,
    } = this.props;

    const border = `${borderWidth || 1}px solid ${borderColor || '#e0e0e0'}`;
    const rowId = row?._id;

    const cells = (rteColumns || []).map((column) => {
      return (
        <TableFieldRteCell
          key={column._id}
          templateJson={column.value}
          row={row}
          tableColumns={tableColumns}
          fieldPositionTableColumns={fieldPositionTableColumns}
          mode={fieldMode}
          fontSize={fontSize}
          fontColor={fontColor}
          fontStyle={fontStyle}
          fontWeight={fontWeight}
          textAlign={textAlign}
          textTransform={textTransform}
          textDecoration={textDecoration}
          padding={padding}
          borderWidth={borderWidth}
          borderColor={borderColor}
          backgroundColor={backgroundColor}
        />
      );
    });

    return (
      <StyledRow
        id="table-field-row"
        data-testid={`${id}-${rowId}-rte-row`}
        ref={(e) => {
          const { rowRef } = this.state;
          if (!rowRef) this.setState({ rowRef: e });
        }}
      >
        {enableNumber ? (
          <StyledNumberCell fontSize={fontSize} padding={padding} border={border}>
            {rowIndex + 1}
          </StyledNumberCell>
        ) : null}
        {enableMenu ? (
          <TableFieldMenuCell
            id={id}
            cellKey={`${rowId}-menu`}
            disabled={disabled}
            rowId={rowId}
            rowIndex={rowIndex}
            rowPath={rowPath}
            options={rowActions}
            onRowAction={onRowAction}
            width={20}
            fontColor={fontColor}
            padding={0}
            borderColor={borderColor}
            borderWidth={borderWidth}
            backgroundColor={backgroundColor}
            primaryColor={primaryColor}
          />
        ) : null}
        {cells}
      </StyledRow>
    );

  }

}

const arePropsEqual = (prev, next) => {
  if (prev.rowIndex !== next.rowIndex) return false;
  if (prev.rowPath !== next.rowPath) return false;
  if (prev.fieldMode !== next.fieldMode) return false;
  if (prev.disabled !== next.disabled) return false;
  if (prev.enableNumber !== next.enableNumber) return false;
  if (prev.enableMenu !== next.enableMenu) return false;
  if (prev.fontSize !== next.fontSize) return false;
  if (prev.fontColor !== next.fontColor) return false;
  if (prev.fontStyle !== next.fontStyle) return false;
  if (prev.fontWeight !== next.fontWeight) return false;
  if (prev.textAlign !== next.textAlign) return false;
  if (prev.textTransform !== next.textTransform) return false;
  if (prev.textDecoration !== next.textDecoration) return false;
  if (prev.padding !== next.padding) return false;
  if (prev.borderWidth !== next.borderWidth) return false;
  if (prev.borderColor !== next.borderColor) return false;
  if (prev.backgroundColor !== next.backgroundColor) return false;
  if (prev.primaryColor !== next.primaryColor) return false;
  if (prev.row !== next.row && !isEqual(prev.row, next.row)) return false;
  if (prev.rteColumns !== next.rteColumns && !isEqual(prev.rteColumns, next.rteColumns)) return false;
  if (prev.tableColumns !== next.tableColumns && !isEqual(prev.tableColumns, next.tableColumns)) return false;
  if (prev.fieldPositionTableColumns !== next.fieldPositionTableColumns && !isEqual(prev.fieldPositionTableColumns, next.fieldPositionTableColumns)) return false;
  return true;
};

export default memo(TableFieldRteRow, arePropsEqual);
